// A link opens lines of a template when an option is chosen: every linked
// line shows when its option is chosen (`NoteLine.ShowWhen`).

export type TemplateLinkTarget = { blockId: string; value: string };

// A line of the template as links read it: the blocks it holds and the
// options that open it.
export type TemplateLinkGraphLine = {
  lineId: string;
  blockIds: string[];
  showWhen: TemplateLinkTarget[];
};

// An option a line can be linked to, named by its text and its section.
export type TemplateLinkOption = TemplateLinkTarget & {
  label: string;
  sectionTitle: string;
};

export const isSameTemplateLinkTarget = (
  first: TemplateLinkTarget,
  second: TemplateLinkTarget,
) => first.blockId === second.blockId && first.value === second.value;

// A condition that opens a line, as `NoteLine.ShowWhen` holds it.
export type TemplateLinkCondition = { BlockID: string; Values: string[] };

// A change of the options that open lines: one added, one removed, or with
// both one replaced by another.
export type TemplateLinkChange = {
  added?: TemplateLinkTarget;
  removed?: TemplateLinkTarget;
};

// The conditions after a change; the other options stay. An added option
// joins its block's condition, or a new one after the others. A condition
// left without values goes, as scribe refuses an empty one.
export const changeTemplateLinkConditions = (
  showWhen: TemplateLinkCondition[],
  { added, removed }: TemplateLinkChange,
): TemplateLinkCondition[] => {
  const kept = showWhen.map((condition) =>
    condition.BlockID === removed?.blockId
      ? {
          ...condition,
          Values: condition.Values.filter((value) => value !== removed.value),
        }
      : condition,
  );
  const withAdded =
    added && !kept.some((condition) => condition.BlockID === added.blockId)
      ? [...kept, { BlockID: added.blockId, Values: [added.value] }]
      : kept.map((condition) =>
          condition.BlockID === added?.blockId
            ? {
                ...condition,
                Values: [...new Set([...condition.Values, added.value])],
              }
            : condition,
        );
  return withAdded.filter((condition) => condition.Values.length > 0);
};

const listOpenedLineIds = (
  graph: TemplateLinkGraphLine[],
  lineIds: string[],
) => {
  const blockIds = graph
    .filter((line) => lineIds.includes(line.lineId))
    .flatMap((line) => line.blockIds);
  return graph
    .filter((line) =>
      line.showWhen.some((condition) => blockIds.includes(condition.blockId)),
    )
    .map((line) => line.lineId);
};

// The lines given and every line they open, directly or through others.
const collectOpenedLineIds = (
  graph: TemplateLinkGraphLine[],
  frontier: string[],
  reached: string[],
): string[] => {
  const next = listOpenedLineIds(graph, frontier).filter(
    (lineId, index, all) =>
      !reached.includes(lineId) && all.indexOf(lineId) === index,
  );
  return next.length
    ? collectOpenedLineIds(graph, next, [...reached, ...next])
    : reached;
};

// Whether the lines may be linked to an option: not to one they hold, nor to
// one on a line they open, which would make the line open its own opener.
// An option of a block the template does not hold is not a target either.
export const createTemplateLinkCheck = ({
  graph,
  lineIds,
}: {
  graph: TemplateLinkGraphLine[];
  lineIds: string[];
}) => {
  const lockedLineIds = collectOpenedLineIds(graph, lineIds, lineIds);
  return (target: TemplateLinkTarget) => {
    const home = graph.find((line) => line.blockIds.includes(target.blockId));
    return !!home && !lockedLineIds.includes(home.lineId);
  };
};

export const listTemplateLinkTargets = ({
  graph,
  lineIds,
  options,
}: {
  graph: TemplateLinkGraphLine[];
  lineIds: string[];
  options: TemplateLinkOption[];
}) => options.filter(createTemplateLinkCheck({ graph, lineIds }));

const normalizeSearchText = (text: string) =>
  text.toLocaleLowerCase().replace(/ё/g, 'е').replace(/\s+/g, ' ').trim();

// Every word of the query is found in the option's text or its section's
// title. Options that hold every word in their own text come first; each
// group keeps the order of the document.
export const searchTemplateLinkOptions = (
  options: TemplateLinkOption[],
  query: string,
) => {
  const words = normalizeSearchText(query).split(' ').filter(Boolean);
  if (!words.length) {
    return options;
  }
  const holdsWords = (text: string) =>
    words.every((word) => normalizeSearchText(text).includes(word));
  const byLabel = options.filter((option) => holdsWords(option.label));
  const bySection = options.filter(
    (option) =>
      !byLabel.includes(option) &&
      holdsWords(`${option.label} ${option.sectionTitle}`),
  );
  return [...byLabel, ...bySection];
};
